import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useMeasure, { type RectReadOnly } from 'react-use-measure';
import { useGetOnboardingRewardsMutation } from '@api/queries/user';
import { groupedSteps } from '@common/config/intro-steps.ts';

import { Playdeck } from '@/common/lib/playdeck';
import { endIntro, updateLevel } from '@/redux/reducers/userReducer';
import store, { useAppSelector } from '@/redux/store';

interface UseIntroGuideReturnType {
  isFirstVisit?: boolean;
  activeStep: number;
  setActiveStep: Dispatch<SetStateAction<number>>;
  highlightComponentRect: RectReadOnly | null;
  setHighlightComponentRect: Dispatch<SetStateAction<RectReadOnly | null>>;
  toNextStep: () => void;
  isIntroActive: boolean;
  setIsIntroActive: Dispatch<SetStateAction<boolean>>;
  activeGroup: string | null;
  setActiveGroup: Dispatch<SetStateAction<string | null>>;
  startOnboarding: (groupName: string) => void;
  stopOnboarding: () => void;
  registerStepCallback: (step: number, fn: () => void) => void;
  finishIntroPlants: boolean;
  finishOnboarding: () => void;
  isFinishOnboarding: boolean;
  helpIconRef: (node: HTMLElement | null) => void;
  helpIconBounds: RectReadOnly;
  acountRef: (node: HTMLElement | null) => void;
  acountBounds: RectReadOnly;
  tgemIconRef: (node: HTMLElement | null) => void;
  tgemIconBounds: RectReadOnly;
}

const useIntroGuide = (): UseIntroGuideReturnType => {
  const userPlants = useAppSelector((state) => state.user.user?.plants);
  const userId = useAppSelector((state) => state.user.user?.id);
  const [getOnboardingRewards] = useGetOnboardingRewardsMutation();
  const isFirstVisit = useAppSelector((state) => state.user.isFirstVisit);
  const navigate = useNavigate();
  const [stepCallbacks, setStepCallbacks] = useState<Map<number, () => void>>(
    new Map(),
  );
  const [activeStep, setActiveStep] = useState(0);
  const [highlightComponentRect, setHighlightComponentRect] =
    useState<RectReadOnly | null>(null);
  const [activeGroup, setActiveGroup] = useState<string | null>(null);
  const [isIntroActive, setIsIntroActive] = useState(false);
  const [finishIntroPlants, setFinishIntroPlants] = useState(false);
  const [isFinishOnboarding, setFinishOnboarding] = useState(false);
  const [helpIconRef, helpIconBounds] = useMeasure();
  const [tgemIconRef, tgemIconBounds] = useMeasure();
  const [acountRef, acountBounds] = useMeasure();
  const dispatch = useDispatch();

  useEffect(() => {
    const refuse = localStorage.getItem(`refuseOnboarding_user_${userId}`);

    if (refuse) {
      finishOnboarding();
    }
  }, []);

  useEffect(() => {
    if (!userPlants) return;

    const plantTypes = Object.keys(userPlants);
    const requiredTypes = ['furniture_factory', 'lumber', 'wood'];
    const hasAllRequired = requiredTypes.every((type) =>
      plantTypes.includes(type),
    );

    if (hasAllRequired) {
      setFinishIntroPlants(true);
    }
  }, [userPlants]);

  const registerStepCallback = useCallback((step: number, fn: () => void) => {
    setStepCallbacks((prev) => {
      const newMap = new Map(prev);
      newMap.set(step, fn);

      return newMap;
    });
  }, []);

  const toNextStep = useCallback(() => {
    if (!activeGroup) return;

    const steps = groupedSteps[activeGroup];
    const currentIndex = steps.indexOf(activeStep);
    const nextStep = steps[currentIndex + 1];

    Playdeck.sendAnalitics({
      type: 'custom_event',
      name: 'onboarding_step_done',
      user_properties: {},
      event_properties: {
        step: activeStep,
        step_name: '',
      },
    });

    if (currentIndex < steps.length - 1) {
      setActiveStep(nextStep);
      stepCallbacks.get(nextStep)?.();
    } else {
      if (activeStep === 22) {
        if (store.getState().user.user?.level === 0) {
          dispatch(updateLevel(1));
        }
        navigate('/');
        startOnboarding('contracts');

        return;
      }

      if (activeStep === 27) {
        setFinishOnboarding(true);
        dispatch(endIntro());
        getOnboardingRewards({ tgem: 200 });

        setTimeout(() => {
          stopOnboarding();
        }, 1200);

        return;
      }

      if (activeGroup !== 'welcome') {
        navigate('/');
        stopOnboarding();
      }

      stopOnboarding();
    }
  }, [activeStep, activeGroup, stepCallbacks]);

  const startOnboarding = useCallback((groupName: string) => {
    const steps = groupedSteps[groupName];

    if (steps?.length > 0) {
      setIsIntroActive(true);
      setActiveGroup(groupName);
      setActiveStep(steps[0]);
    }
  }, []);

  const stopOnboarding = useCallback(() => {
    setActiveStep(0);
    setActiveGroup(null);
    setIsIntroActive(false);
  }, []);

  const finishOnboarding = useCallback(() => {
    setActiveStep(0);
    setActiveGroup(null);
    setIsIntroActive(false);
    setFinishIntroPlants(true);
    dispatch(endIntro());
  }, []);

  return {
    isFirstVisit,
    activeStep: isIntroActive ? activeStep : 0,
    setActiveStep,
    highlightComponentRect,
    setHighlightComponentRect,
    toNextStep,
    isIntroActive,
    setIsIntroActive,
    activeGroup,
    setActiveGroup,
    startOnboarding,
    stopOnboarding,
    registerStepCallback,
    finishIntroPlants,
    finishOnboarding,
    isFinishOnboarding,
    helpIconRef,
    helpIconBounds,
    acountRef,
    acountBounds,
    tgemIconRef,
    tgemIconBounds,
  };
};

export { useIntroGuide };
